import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Image as ImageIcon,
  Home,
  Wrench,
  Images,
  Info,
  Mail,
  Phone,
  Lock,
  Settings,
  Save,
  GripVertical,
} from "lucide-react";
import SEO from "@/components/SEO";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Admin sections configuration
const SECTIONS = [
  { id: "forside", name: "Forside", icon: Home },
  { id: "ydelser", name: "Ydelser", icon: Wrench },
  { id: "galleri", name: "Galleri", icon: Images },
  { id: "om-os", name: "Om os", icon: Info },
];

// Gallery sub-categories - REMOVED, now using single flat gallery
// const GALLERY_CATEGORIES = [...];

const AdminPage = () => {
  const [galleries, setGalleries] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [activeSection, setActiveSection] = useState("forside");
  const [selectedSlug, setSelectedSlug] = useState("");
  const [loading, setLoading] = useState(true);
  const [newImageUrl, setNewImageUrl] = useState("");
  const [newImageAlt, setNewImageAlt] = useState("");
  const [newImageCaption, setNewImageCaption] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  
  // Drag and drop state
  const [draggedImageId, setDraggedImageId] = useState(null);
  const [dragOverImageId, setDragOverImageId] = useState(null);
  
  // Site config state
  const [siteConfig, setSiteConfig] = useState({ 
    phone: "", 
    email: "", 
    cvr: "",
    heroImageDesktopUrl: "",
    heroImageMobileUrl: ""
  });
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  
  // Track if initial selection has been made (prevents reset on data refresh)
  const [hasInitialSelection, setHasInitialSelection] = useState(false);
  
  // Ref for scroll position preservation
  const scrollPositionRef = useRef(0);
  const contentAreaRef = useRef(null);
  
  // Save scroll position before mutations
  const saveScrollPosition = useCallback(() => {
    scrollPositionRef.current = window.scrollY;
  }, []);
  
  // Restore scroll position after mutations
  const restoreScrollPosition = useCallback(() => {
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollPositionRef.current);
    });
  }, []);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchGalleries();
      fetchSubmissions();
      fetchSiteConfig();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // Only auto-select first gallery when:
    // 1. User explicitly changes section (not on data refresh)
    // 2. No gallery is currently selected in this section
    if (galleries.length > 0) {
      const sectionGalleries = galleries.filter(g => g.section === activeSection);
      const currentSelectionInSection = sectionGalleries.find(g => g.slug === selectedSlug);
      
      // If current selection is not in this section, select first of this section
      if (!currentSelectionInSection && sectionGalleries.length > 0) {
        setSelectedSlug(sectionGalleries[0].slug);
      }
    }
  }, [activeSection]); // Only run when activeSection changes, NOT when galleries changes

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API}/admin/auth`, {
        credentials: 'include',
      });
      if (response.ok) {
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
    } catch (err) {
      setIsAuthenticated(false);
    } finally {
      setAuthChecking(false);
    }
  };

  const handleLogin = () => {
    const authUrl = `${API}/admin/auth`;
    const authWindow = window.open(authUrl, '_blank', 'width=400,height=300');
    const checkInterval = setInterval(() => {
      if (authWindow?.closed) {
        clearInterval(checkInterval);
        setTimeout(() => {
          setAuthChecking(true);
          checkAuth();
        }, 500);
      }
    }, 200);
  };

  const fetchGalleries = async () => {
    try {
      const res = await axios.get(`${API}/galleries`);
      setGalleries(res.data);
      
      // Only set initial selection on first load (not on refresh after add/delete)
      if (!hasInitialSelection) {
        const forsideGalleries = res.data.filter(g => g.section === "forside");
        if (forsideGalleries.length > 0) {
          setSelectedSlug(forsideGalleries[0].slug);
        }
        setHasInitialSelection(true);
      }
    } catch (err) {
      console.error("Error fetching galleries:", err);
      toast.error("Kunne ikke hente gallerier");
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmissions = async () => {
    try {
      const res = await axios.get(`${API}/contact/submissions`);
      setSubmissions(res.data);
    } catch (err) {
      console.error("Error fetching submissions:", err);
    }
  };

  const fetchSiteConfig = async () => {
    setConfigLoading(true);
    try {
      const res = await axios.get(`${API}/config`);
      setSiteConfig(res.data);
    } catch (err) {
      console.error("Error fetching site config:", err);
    } finally {
      setConfigLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    setConfigSaving(true);
    try {
      const res = await axios.put(`${API}/config`, siteConfig);
      setSiteConfig(res.data);
      toast.success("Indstillinger gemt");
    } catch (err) {
      console.error("Error saving config:", err);
      toast.error("Kunne ikke gemme indstillinger");
    } finally {
      setConfigSaving(false);
    }
  };

  const handleAddImage = async () => {
    if (!newImageUrl || !selectedSlug) return;
    
    // Save current scroll position and selected slug before mutation
    saveScrollPosition();
    const currentSlug = selectedSlug;
    
    try {
      await axios.post(`${API}/galleries/${currentSlug}/images`, {
        url: newImageUrl,
        alt: newImageAlt || "Billede",
        caption: newImageCaption || null,
        is_placeholder: false,
      });
      toast.success("Billede tilføjet");
      setNewImageUrl("");
      setNewImageAlt("");
      setNewImageCaption("");
      setDialogOpen(false);
      
      // Fetch updated data while preserving selection
      await fetchGalleries();
      
      // Restore scroll position after state updates
      restoreScrollPosition();
    } catch (err) {
      console.error("Error adding image:", err);
      toast.error("Kunne ikke tilføje billede");
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!selectedSlug) return;
    
    // Save current scroll position before mutation
    saveScrollPosition();
    const currentSlug = selectedSlug;
    
    try {
      await axios.delete(`${API}/galleries/${currentSlug}/images/${imageId}`);
      toast.success("Billede slettet");
      
      // Fetch updated data while preserving selection
      await fetchGalleries();
      
      // Restore scroll position after state updates
      restoreScrollPosition();
    } catch (err) {
      console.error("Error deleting image:", err);
      toast.error("Kunne ikke slette billede");
    }
  };

  const handleDeleteSubmission = async (submissionId) => {
    try {
      await axios.delete(`${API}/contact/submissions/${submissionId}`);
      toast.success("Henvendelse slettet");
      fetchSubmissions();
    } catch (err) {
      console.error("Error deleting submission:", err);
      toast.error("Kunne ikke slette henvendelse");
    }
  };

  const moveImage = async (imageId, direction) => {
    if (!selectedSlug) return;
    const currentGallery = galleries.find(g => g.slug === selectedSlug);
    if (!currentGallery) return;
    
    const images = currentGallery.images || [];
    const currentIndex = images.findIndex(img => img.id === imageId);
    
    if (currentIndex === -1) return;
    if (direction === "up" && currentIndex === 0) return;
    if (direction === "down" && currentIndex === images.length - 1) return;
    
    const newImages = [...images];
    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    const temp = newImages[currentIndex];
    newImages[currentIndex] = newImages[newIndex];
    newImages[newIndex] = temp;
    
    const newOrder = newImages.map(img => img.id);
    
    // Save scroll position before mutation
    saveScrollPosition();
    
    try {
      await axios.put(`${API}/galleries/${selectedSlug}/reorder`, newOrder);
      toast.success("Rækkefølge opdateret");
      
      // Fetch updated data while preserving selection
      await fetchGalleries();
      
      // Restore scroll position after state updates
      restoreScrollPosition();
    } catch (err) {
      console.error("Error reordering:", err);
      toast.error("Kunne ikke ændre rækkefølge");
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, imageId) => {
    setDraggedImageId(imageId);
    e.dataTransfer.effectAllowed = "move";
    // Add a slight delay to allow the drag image to be captured
    setTimeout(() => {
      e.target.style.opacity = "0.5";
    }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = "1";
    setDraggedImageId(null);
    setDragOverImageId(null);
  };

  const handleDragOver = (e, imageId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (imageId !== draggedImageId) {
      setDragOverImageId(imageId);
    }
  };

  const handleDragLeave = () => {
    setDragOverImageId(null);
  };

  const handleDrop = async (e, targetImageId) => {
    e.preventDefault();
    setDragOverImageId(null);
    
    if (!draggedImageId || draggedImageId === targetImageId || !selectedSlug) {
      setDraggedImageId(null);
      return;
    }
    
    const currentGallery = galleries.find(g => g.slug === selectedSlug);
    if (!currentGallery) return;
    
    const images = [...(currentGallery.images || [])];
    const draggedIndex = images.findIndex(img => img.id === draggedImageId);
    const targetIndex = images.findIndex(img => img.id === targetImageId);
    
    if (draggedIndex === -1 || targetIndex === -1) return;
    
    // Remove dragged item and insert at target position
    const [draggedItem] = images.splice(draggedIndex, 1);
    images.splice(targetIndex, 0, draggedItem);
    
    const newOrder = images.map(img => img.id);
    
    // Save scroll position
    saveScrollPosition();
    
    try {
      await axios.put(`${API}/galleries/${selectedSlug}/reorder`, newOrder);
      toast.success("Rækkefølge opdateret");
      await fetchGalleries();
      restoreScrollPosition();
    } catch (err) {
      console.error("Error reordering:", err);
      toast.error("Kunne ikke ændre rækkefølge");
    }
    
    setDraggedImageId(null);
  };

  const currentGallery = galleries.find(g => g.slug === selectedSlug);
  const currentImages = currentGallery ? (currentGallery.images || []) : [];
  const sectionGalleries = galleries.filter(g => g.section === activeSection);

  // Auth checking state
  if (authChecking) {
    return (
      <div data-testid="admin-page" className="pt-20 min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <SEO title="Admin | Birk Anlæg" description="Admin panel" canonical="/admin" noindex={true} />
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#1A3C34] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Logger ind...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <div data-testid="admin-page" className="pt-20 min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <SEO title="Admin Login | Birk Anlæg" description="Admin login" canonical="/admin" noindex={true} />
        <div className="text-center bg-white p-8 rounded-xl shadow-sm max-w-md">
          <Lock size={48} className="mx-auto mb-4 text-[#1A3C34]" />
          <h2 className="text-2xl font-semibold text-[#1A3C34] mb-2" style={{ fontFamily: "Manrope, sans-serif" }}>
            Adgangskode påkrævet
          </h2>
          <p className="text-gray-600 mb-6">
            Log ind for at få adgang til admin-panelet.
          </p>
          <button
            onClick={handleLogin}
            data-testid="login-button"
            className="bg-[#1A3C34] text-white px-6 py-3 rounded-full font-medium hover:bg-[#142F29] transition-colors"
          >
            Log ind
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="pt-20 min-h-screen bg-[#F9FAFB]">
      <SEO title="Admin Panel | Birk Anlæg" description="Admin panel for billedhåndtering" canonical="/admin" noindex={true} />
      {/* Header */}
      <div className="bg-[#1A3C34] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1
            data-testid="admin-title"
            className="text-3xl md:text-4xl font-bold text-white"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Admin Panel
          </h1>
          <p className="text-gray-300 mt-2">
            Administrer billeder efter side
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="images" className="w-full">
          <TabsList className="mb-8">
            <TabsTrigger value="images" data-testid="tab-images">
              <ImageIcon size={18} className="mr-2" />
              Billeder
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">
              <Settings size={18} className="mr-2" />
              Indstillinger
            </TabsTrigger>
            <TabsTrigger value="submissions" data-testid="tab-submissions">
              <Mail size={18} className="mr-2" />
              Henvendelser ({submissions.length})
            </TabsTrigger>
          </TabsList>

          {/* Images Tab */}
          <TabsContent value="images">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Section Sidebar */}
              <div className="lg:col-span-1 space-y-4">
                {/* Section buttons */}
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <h3 className="font-semibold text-[#1A3C34] mb-4 text-sm uppercase tracking-wider">
                    Sider
                  </h3>
                  <div className="space-y-1">
                    {SECTIONS.map((section) => {
                      const Icon = section.icon;
                      return (
                        <button
                          key={section.id}
                          onClick={() => {
                            setActiveSection(section.id);
                          }}
                          data-testid={`section-btn-${section.id}`}
                          className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors flex items-center gap-3 ${
                            activeSection === section.id
                              ? "bg-[#1A3C34] text-white"
                              : "hover:bg-gray-100 text-gray-700"
                          }`}
                        >
                          <Icon size={18} />
                          <span>{section.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Sub-galleries for current section */}
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <h3 className="font-semibold text-[#1A3C34] mb-4 text-sm uppercase tracking-wider flex items-center gap-2">
                    Gallerier
                  </h3>
                  
                  {loading ? (
                    <div className="space-y-2">
                      <div className="h-10 bg-gray-100 animate-pulse rounded" />
                      <div className="h-10 bg-gray-100 animate-pulse rounded" />
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {sectionGalleries.map((gallery) => (
                        <button
                          key={gallery.slug}
                          onClick={() => setSelectedSlug(gallery.slug)}
                          data-testid={`gallery-btn-${gallery.slug}`}
                          className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors flex items-center justify-between ${
                            selectedSlug === gallery.slug
                              ? "bg-[#1A3C34] text-white"
                              : "hover:bg-gray-100 text-gray-700"
                          }`}
                        >
                          <span className="truncate">{gallery.name}</span>
                          <span className={`text-xs ${selectedSlug === gallery.slug ? "text-gray-300" : "text-gray-400"}`}>
                            {gallery.images?.length || 0}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Image Grid */}
              <div className="lg:col-span-3">
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2
                        className="text-xl font-semibold text-[#1A3C34]"
                        style={{ fontFamily: "Manrope, sans-serif" }}
                      >
                        {currentGallery?.name || "Vælg et galleri"}
                      </h2>
                      {currentGallery?.description && (
                        <p className="text-gray-500 text-sm mt-1">
                          {currentGallery.description}
                        </p>
                      )}
                    </div>

                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                      <DialogTrigger asChild>
                        <button
                          data-testid="add-image-btn"
                          disabled={!selectedSlug}
                          className="flex items-center gap-2 bg-[#1A3C34] text-white px-4 py-2 rounded-full font-medium hover:bg-[#142F29] transition-colors disabled:opacity-50"
                        >
                          <Plus size={18} />
                          Tilføj billede
                        </button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Tilføj nyt billede</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Billede URL *
                            </label>
                            <input
                              type="url"
                              data-testid="new-image-url"
                              value={newImageUrl}
                              onChange={(e) => setNewImageUrl(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-[#1A3C34] focus:ring-1 focus:ring-[#1A3C34] outline-none"
                              placeholder="https://..."
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Alt tekst
                            </label>
                            <input
                              type="text"
                              data-testid="new-image-alt"
                              value={newImageAlt}
                              onChange={(e) => setNewImageAlt(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-[#1A3C34] focus:ring-1 focus:ring-[#1A3C34] outline-none"
                              placeholder="Beskrivelse af billedet"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Billedtekst
                            </label>
                            <input
                              type="text"
                              data-testid="new-image-caption"
                              value={newImageCaption}
                              onChange={(e) => setNewImageCaption(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-[#1A3C34] focus:ring-1 focus:ring-[#1A3C34] outline-none"
                              placeholder="F.eks. Terrasse i København"
                            />
                          </div>
                          {newImageUrl && (
                            <div className="mt-4">
                              <p className="text-sm text-gray-500 mb-2">Forhåndsvisning:</p>
                              <img
                                src={newImageUrl}
                                alt="Preview"
                                className="w-full h-40 object-cover rounded-lg bg-gray-100"
                              />
                            </div>
                          )}
                          <button
                            onClick={handleAddImage}
                            data-testid="save-image-btn"
                            disabled={!newImageUrl}
                            className="w-full bg-[#1A3C34] text-white py-2.5 rounded-full font-medium hover:bg-[#142F29] transition-colors disabled:opacity-50"
                          >
                            Tilføj billede
                          </button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>

                  {/* Images Grid */}
                  {currentImages.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {currentImages.map((image, index) => {
                        // Service reference labels for Ydelser gallery
                        const serviceLabels = [
                          "Flisebelægning",
                          "Natursten",
                          "Rullegræs", 
                          "Hegn",
                          "Træfældning",
                          "Rodfræsning",
                          "Anlægsopgaver"
                        ];
                        const isYdelserGallery = selectedSlug === "ydelser";
                        const serviceLabel = isYdelserGallery && serviceLabels[index] ? serviceLabels[index] : null;
                        
                        return (
                        <div
                          key={image.id}
                          data-testid={`admin-image-${index}`}
                          draggable
                          onDragStart={(e) => handleDragStart(e, image.id)}
                          onDragEnd={handleDragEnd}
                          onDragOver={(e) => handleDragOver(e, image.id)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, image.id)}
                          className={`group relative bg-gray-100 rounded-lg overflow-hidden cursor-move transition-all duration-200 ${
                            dragOverImageId === image.id ? "ring-2 ring-[#1A3C34] ring-offset-2 scale-105" : ""
                          } ${draggedImageId === image.id ? "opacity-50" : ""}`}
                        >
                          {/* Service reference label - shown above image for Ydelser */}
                          {serviceLabel && (
                            <div className="absolute top-0 left-0 right-0 z-20 bg-[#1A3C34] text-white text-xs font-medium py-1.5 px-2 text-center truncate">
                              {serviceLabel}
                            </div>
                          )}
                          
                          {/* Drag handle - always visible */}
                          <div className={`absolute ${serviceLabel ? 'top-10' : 'top-2'} right-2 z-10 p-1.5 bg-white/90 hover:bg-white rounded-lg shadow-sm cursor-grab active:cursor-grabbing`}>
                            <GripVertical size={16} className="text-gray-500" />
                          </div>
                          
                          {/* Order number badge */}
                          <div className={`absolute ${serviceLabel ? 'top-10' : 'top-2'} left-2 z-10 w-6 h-6 bg-[#1A3C34] text-white text-xs font-medium rounded-full flex items-center justify-center`}>
                            {index + 1}
                          </div>
                          
                          <div className={`aspect-square ${serviceLabel ? 'mt-7' : ''}`}>
                            <img
                              src={image.url}
                              alt={image.alt || ""}
                              className="w-full h-full object-cover pointer-events-none"
                            />
                          </div>

                          {/* Overlay controls */}
                          <div className={`absolute inset-0 ${serviceLabel ? 'top-7' : ''} bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2`}>
                            <div className="flex gap-2">
                              <button
                                onClick={() => moveImage(image.id, "up")}
                                disabled={index === 0}
                                className="p-2 bg-white/20 hover:bg-white/30 rounded-lg text-white disabled:opacity-30 transition-colors"
                                title="Flyt op"
                              >
                                ↑
                              </button>
                              <button
                                onClick={() => moveImage(image.id, "down")}
                                disabled={index === currentImages.length - 1}
                                className="p-2 bg-white/20 hover:bg-white/30 rounded-lg text-white disabled:opacity-30 transition-colors"
                                title="Flyt ned"
                              >
                                ↓
                              </button>
                            </div>
                            <button
                              onClick={() => handleDeleteImage(image.id)}
                              data-testid={`delete-image-${index}`}
                              className="p-2 bg-red-500/80 hover:bg-red-600 rounded-lg text-white transition-colors"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>

                          {/* Placeholder badge */}
                          {image.is_placeholder && (
                            <span className={`absolute ${serviceLabel ? 'top-16' : 'top-10'} left-2 bg-black/70 text-white text-xs px-2 py-1 rounded`}>
                              Placeholder
                            </span>
                          )}

                          {/* Caption */}
                          {image.caption && (
                            <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-2 truncate">
                              {image.caption}
                            </div>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-16 text-gray-500">
                      <ImageIcon size={48} className="mx-auto mb-4 opacity-30" />
                      <p>Ingen billeder i dette galleri endnu.</p>
                      <p className="text-sm mt-1">
                        Klik &quot;Tilføj billede&quot; for at komme i gang.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="max-w-2xl space-y-8">
              {/* Hero Images Section */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h2
                  className="text-xl font-semibold text-[#1A3C34] mb-6"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  <Home size={20} className="inline mr-2" />
                  Hero-billeder (Forside)
                </h2>
                <p className="text-gray-500 text-sm mb-6">
                  Separate billeder til desktop og mobil. Hvis mobil-billedet er tomt, bruges desktop-billedet.
                </p>

                {configLoading ? (
                  <div className="space-y-4">
                    <div className="h-12 bg-gray-100 animate-pulse rounded" />
                    <div className="h-12 bg-gray-100 animate-pulse rounded" />
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        <ImageIcon size={14} className="inline mr-2" />
                        Hero-billede (Desktop)
                      </label>
                      <input
                        type="url"
                        data-testid="config-hero-desktop"
                        value={siteConfig.heroImageDesktopUrl || ""}
                        onChange={(e) => setSiteConfig({ ...siteConfig, heroImageDesktopUrl: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1A3C34] focus:ring-1 focus:ring-[#1A3C34] outline-none"
                        placeholder="https://..."
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        Anbefalet størrelse: 1920x1080 pixels eller større.
                      </p>
                      {siteConfig.heroImageDesktopUrl && (
                        <div className="mt-3">
                          <img
                            src={siteConfig.heroImageDesktopUrl}
                            alt="Desktop hero preview"
                            className="w-full h-32 object-cover rounded-lg bg-gray-100"
                          />
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        <ImageIcon size={14} className="inline mr-2" />
                        Hero-billede (Mobil)
                      </label>
                      <input
                        type="url"
                        data-testid="config-hero-mobile"
                        value={siteConfig.heroImageMobileUrl || ""}
                        onChange={(e) => setSiteConfig({ ...siteConfig, heroImageMobileUrl: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1A3C34] focus:ring-1 focus:ring-[#1A3C34] outline-none"
                        placeholder="https://..."
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        Anbefalet størrelse: 768x1024 pixels. Lad feltet være tomt for at bruge desktop-billedet.
                      </p>
                      {siteConfig.heroImageMobileUrl && (
                        <div className="mt-3">
                          <img
                            src={siteConfig.heroImageMobileUrl}
                            alt="Mobile hero preview"
                            className="w-32 h-48 object-cover rounded-lg bg-gray-100"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Contact Info Section */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h2
                  className="text-xl font-semibold text-[#1A3C34] mb-6"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  Kontaktoplysninger
                </h2>
                <p className="text-gray-500 text-sm mb-6">
                  Disse oplysninger vises på hele hjemmesiden: header, footer og kontaktside.
                </p>

                {configLoading ? (
                  <div className="space-y-4">
                    <div className="h-12 bg-gray-100 animate-pulse rounded" />
                    <div className="h-12 bg-gray-100 animate-pulse rounded" />
                    <div className="h-12 bg-gray-100 animate-pulse rounded" />
                  </div>
                ) : (
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        <Phone size={14} className="inline mr-2" />
                        Telefonnummer
                      </label>
                      <input
                        type="tel"
                        data-testid="config-phone"
                        value={siteConfig.phone}
                        onChange={(e) => setSiteConfig({ ...siteConfig, phone: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1A3C34] focus:ring-1 focus:ring-[#1A3C34] outline-none"
                        placeholder="+45 12 34 56 78"
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        Vises i header, footer og kontaktside. Bruges også til &quot;klik-for-at-ringe&quot;.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        <Mail size={14} className="inline mr-2" />
                        Email
                      </label>
                      <input
                        type="email"
                        data-testid="config-email"
                        value={siteConfig.email}
                        onChange={(e) => setSiteConfig({ ...siteConfig, email: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1A3C34] focus:ring-1 focus:ring-[#1A3C34] outline-none"
                        placeholder="info@birk-anlaeg.dk"
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        Vises i footer og kontaktside.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        CVR-nummer
                      </label>
                      <input
                        type="text"
                        data-testid="config-cvr"
                        value={siteConfig.cvr}
                        onChange={(e) => setSiteConfig({ ...siteConfig, cvr: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#1A3C34] focus:ring-1 focus:ring-[#1A3C34] outline-none"
                        placeholder="12345678"
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        Vises i footeren.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Save Button */}
              <button
                onClick={handleSaveConfig}
                disabled={configSaving}
                data-testid="save-config-btn"
                className="w-full flex items-center justify-center gap-2 bg-[#1A3C34] text-white px-6 py-3 rounded-full font-medium hover:bg-[#142F29] transition-colors disabled:opacity-50"
              >
                {configSaving ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Gemmer...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    Gem alle indstillinger
                  </>
                )}
              </button>
            </div>
          </TabsContent>

          {/* Submissions Tab */}
          <TabsContent value="submissions">
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Dato</TableHead>
                    <TableHead>Navn</TableHead>
                    <TableHead>Kontakt</TableHead>
                    <TableHead>Besked</TableHead>
                    <TableHead>Email sendt</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {submissions.length > 0 ? (
                    submissions.map((sub) => (
                      <TableRow key={sub.id} data-testid={`submission-${sub.id}`}>
                        <TableCell className="whitespace-nowrap">
                          {new Date(sub.created_at).toLocaleDateString("da-DK")}
                        </TableCell>
                        <TableCell className="font-medium">{sub.name}</TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <a
                              href={`tel:${sub.phone}`}
                              className="flex items-center gap-1 text-sm text-gray-600 hover:text-[#1A3C34]"
                            >
                              <Phone size={14} />
                              {sub.phone}
                            </a>
                            <a
                              href={`mailto:${sub.email}`}
                              className="flex items-center gap-1 text-sm text-gray-600 hover:text-[#1A3C34]"
                            >
                              <Mail size={14} />
                              {sub.email}
                            </a>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          {sub.message}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`px-2 py-1 rounded-full text-xs ${
                              sub.email_sent
                                ? "bg-green-100 text-green-700"
                                : "bg-yellow-100 text-yellow-700"
                            }`}
                          >
                            {sub.email_sent ? "Ja" : "Nej"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <button
                            onClick={() => handleDeleteSubmission(sub.id)}
                            data-testid={`delete-submission-${sub.id}`}
                            className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <Trash2 size={18} />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-12 text-gray-500">
                        Ingen henvendelser endnu.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;
